import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResidueType } from "../../types/residue.type";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { DownOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";

const { Title } = Typography;
const { Search } = Input;

export default function Price() {
  const { type } = useParams();
  const [data, setData] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<ResidueType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/residue", {
        params: {
          page,
          type,
          limit: pageSize,
          search: searchTerm,
        },
      });

      const { data, total } = response.data;

      setData(data);
      setTotal(total);
    } catch (error) {
      console.log(error);
      message.error("Ошибка получение остатка.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchTerm, type]);

  const handleOpenModal = (item: ResidueType) => {
    setSelectedItem(item);
    form.setFieldsValue({
      amount: item.price[item.price.currency],
      currency: item.price.currency,
      exchangeRate: item.price.exchangeRate,
      lowTradePrice: item.lowTradePrice
        ? item.lowTradePrice[item.price.currency]
        : undefined,
      upTradePrice: item.upTradePrice
        ? item.upTradePrice[item.price.currency]
        : undefined,
    });
    setIsModalOpen(true);
  };

  const handleExport = () => {
    downloadFile("/export/residues", "residue_data.xlsx", { type });
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Ед. изм.",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Цена USD",
      key: "priceUSD",
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.USD !== undefined
          ? `${record.price.USD}`
          : "Н/Д";
      },
    },
    {
      title: "Сумма USD",
      key: "totalUSD",
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.USD && record.residue !== undefined
          ? `${roundToThreeDecimals(record.price.USD * record.residue)}`
          : "Н/Д";
      },
    },
    {
      title: "Цена UZS",
      key: "priceUZS",
      render: (_: any, record: ResidueType) => {
        return record.price && record.price.UZS !== undefined
          ? `${record.price.UZS}`
          : "Н/Д";
      },
    },
    {
      title: "Средняя Себестоимость USD",
      key: "averageCostUSD",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.averageCostUSD} USD`
          : "Н/Д";
      },
    },
    {
      title: "Средняя Себестоимость UZS",
      key: "averageCostUZS",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.averageCostUZS} UZS`
          : "Н/Д";
      },
    },
    {
      title: "Прибыль (%) USD",
      key: "profitPercentageUSD",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.profitPercentageUSD}%`
          : "Н/Д";
      },
    },
    {
      title: "Прибыль (%) UZS",
      key: "profitPercentageUZS",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${record.averageCost.profitPercentageUZS}%`
          : "Н/Д";
      },
    },
    {
      title: "Цена торговли USD",
      key: "tradePriceUSD",
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.upTradePrice !== undefined
          ? `${record.lowTradePrice.USD} - ${record.upTradePrice.USD}`
          : "Н/Д";
      },
    },
    {
      title: "Цена торговли UZS",
      key: "tradePriceUSD",
      render: (_: any, record: ResidueType) => {
        return record.lowTradePrice && record.upTradePrice !== undefined
          ? `${record.lowTradePrice.UZS} - ${record.upTradePrice.UZS}`
          : "Н/Д";
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: ResidueType) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleOpenModal(record)}>
              Изменить
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleSubmitForm = async (values: {
    currency: Currency;
    amount: number;
    lowTradePrice: number;
    upTradePrice: number;
    exchangeRate: number;
  }) => {
    setConfirmLoading(true);

    const data = {
      ...values,
      type,
    };
    try {
      await api.post(`/price/${selectedItem?._id}`, data);

      message.success("Цены обновлены");
      form.resetFields();
      fetchData();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving price:", error);
      message.error(error.response.data.error);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Прайсы</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select placeholder="Выберите валюту">
              {Object.values(Currency).map((currency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="lowTradePrice"
            label="Цена торговли от"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="upTradePrice"
            label="Цена торговли до"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label="Курс"
            rules={[{ required: true, message: "Введите курс" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
